/**
 * Animation Constants
 * Shared animation durations, delays, easing functions, and stagger patterns
 * @module @shared/constants
 */

/**
 * Standard animation durations (in seconds)
 */
export const ANIMATION_DURATIONS = {
  INSTANT: 0.1,
  FAST: 0.2,
  BASE: 0.25,
  NORMAL: 0.3,
  SLOW: 0.4,
  SLOWER: 0.5,
  VERY_SLOW: 1,
} as const;

/**
 * Standard animation delays (in seconds)
 * Used for staggered animations and sequential effects
 */
export const ANIMATION_DELAYS = {
  NONE: 0,
  MICRO: 0.03,
  TINY: 0.05,
  SMALL: 0.1,
  BASE: 0.15,
  MEDIUM: 0.2,
  LARGE: 0.3,
  EXTRA_LARGE: 0.5,
} as const;

/**
 * Stagger delay multiplier
 * Use with index: delayIndex * STAGGER_DELAY_MULTIPLIER
 */
export const STAGGER_DELAY_MULTIPLIER = 0.05;

/**
 * Standard easing functions
 */
export const EASING = {
  // [x1, y1, x2, y2] for cubic-bezier
  EASE_OUT_CUBIC: [0.4, 0, 0.2, 1] as const,
  EASE_IN_OUT: [0.4, 0, 0.6, 1] as const,
  LINEAR: [0, 0, 1, 1] as const,
} as const;

/**
 * Motion preset combinations
 */
export const MOTION_PRESETS = {
  FADE_IN: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  SLIDE_UP: {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 12 },
  },
  SLIDE_DOWN: {
    initial: { opacity: 0, y: -12 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -12 },
  },
  SCALE_IN: {
    initial: { opacity: 0, scale: 0.98 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.98 },
  },
} as const;

/**
 * Transition configurations
 */
export const TRANSITIONS = {
  DEFAULT: {
    duration: ANIMATION_DURATIONS.BASE,
    ease: EASING.EASE_OUT_CUBIC,
  },
  FAST: {
    duration: ANIMATION_DURATIONS.FAST,
    ease: EASING.EASE_OUT_CUBIC,
  },
  SLOW: {
    duration: ANIMATION_DURATIONS.SLOW,
    ease: EASING.EASE_OUT_CUBIC,
  },
} as const;
